import type { AdminVerticalList } from '../api/types';

/**
 * The create-org form's two decisions, as functions rather than as state
 * scattered through the component — which is what makes them testable in a suite
 * that carries no DOM.
 */

/**
 * The org type the form starts on: **the one the server says it defaults to**.
 *
 * Not a key written down here, and not the first row either. `GET
 * /admin/verticals` publishes `default` because the server is what decides what
 * a create body naming no vertical gets; inferring it from list order would have
 * made a reordered registry silently move the form's default and not the
 * server's.
 *
 * The fallback to the first offered key covers only the case where `default`
 * names something the list does not contain — the form must never sit on a value
 * no option carries, because the select would then show one thing and submit
 * another. An empty string when there is nothing at all; `createOrgBody` then
 * omits the field entirely.
 */
export function defaultVertical(offered: AdminVerticalList): string {
  const offers = (key: string): boolean => offered.verticals.some((row) => row.key === key);
  if (offered.default && offers(offered.default)) return offered.default;
  return offered.verticals[0]?.key ?? '';
}

/**
 * The `POST /admin/orgs` body. Trimmed, and `vertical` **omitted** rather than
 * sent empty when the selector has nothing to offer: an empty string is not a
 * registered key and would earn a 400, where an absent field earns the server's
 * documented default.
 */
export function createOrgBody(input: { slug: string; name: string; vertical: string }): {
  slug: string;
  name: string;
  vertical?: string;
} {
  const vertical = input.vertical.trim();
  return {
    slug: input.slug.trim(),
    name: input.name.trim(),
    ...(vertical.length > 0 ? { vertical } : {}),
  };
}
